import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MessageCircle, Search, Plus, UserCheck } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientConversationsListPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/messages/conversations`);
      setConversations(res.data);
    } catch (error) { console.error('Failed to load conversations:', error); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);


  const startDistributorChat = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/messages/conversations`);
      navigate(`/client/messages/${res.data.id}`);
    } catch (err) {
      if (err.response?.status === 400) {
        // No distributor assigned
      }
    } finally { setCreating(false); }
  };

  const filteredConversations = conversations.filter(c =>
    c.distributor_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const hasDistributorConvo = conversations.some(c => c.distributor_id);

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 rounded-full border-4 border-teal-100 border-t-teal-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-5 animate-fade-in" data-testid="client-conversations-list">
      <div className="pt-1">
        <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Mensajes
        </h1>
        <p className="text-sm text-gray-400 mt-1 font-medium">Tus conversaciones</p>
      </div>

      {/* Search */}
      {conversations.length > 0 && (
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <Input
            placeholder="Buscar conversacion..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-11 bg-white border-gray-100 focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20 rounded-xl h-11 shadow-sm"
            data-testid="search-client-conversations"
          />
        </div>
      )}

      {/* New conversation button */}
      {!hasDistributorConvo && user?.distributor_id && (
        <button
          onClick={startDistributorChat}
          disabled={creating}
          className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-2xl p-4 flex items-center gap-3 shadow-lg shadow-emerald-500/20 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50"
          data-testid="new-distributor-chat-btn"
        >
          <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center">
            <Plus size={22} className="text-white" />
          </div>
          <div className="text-left">
            <p className="font-bold text-sm">Iniciar conversacion con tu distribuidor</p>
            <p className="text-xs text-white/70">Envia tu primer mensaje</p>
          </div>
        </button>
      )}

      {/* Conversations list */}
      {filteredConversations.length === 0 && !(!hasDistributorConvo && user?.distributor_id) ? (
        <div className="bg-white rounded-3xl border border-gray-100/50 shadow-sm p-10 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <MessageCircle size={32} className="text-teal-300" />
          </div>
          <p className="font-bold text-gray-700 text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Sin conversaciones
          </p>
          <p className="text-sm text-gray-400 mt-1">
            {user?.distributor_id
              ? 'Aun no tienes conversaciones activas'
              : 'Necesitas un distribuidor asignado para enviar mensajes'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredConversations.map((convo) => (
            <button
              key={convo.id}
              onClick={() => navigate(`/client/messages/${convo.id}`)}
              className="w-full bg-white rounded-2xl border border-gray-100/50 p-4 text-left shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
              data-testid={`client-convo-${convo.id}`}
            >
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                    <span className="text-white font-bold">{convo.distributor_name?.charAt(0)?.toUpperCase()}</span>
                  </div>
                  {convo.unread_count > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-teal-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-lg">
                      {convo.unread_count}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="font-bold text-gray-900 text-sm truncate">{convo.distributor_name}</p>
                    <span className="text-[10px] text-gray-400 flex-shrink-0 ml-2">{formatTime(convo.last_message_at)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <UserCheck size={11} className="text-emerald-500 flex-shrink-0" />
                    <span className="text-[10px] text-emerald-500 font-medium">Tu distribuidor</span>
                  </div>
                  <p className={`text-xs mt-0.5 truncate ${convo.unread_count > 0 ? 'text-gray-700 font-semibold' : 'text-gray-400'}`}>
                    {convo.last_message || 'Sin mensajes aun'}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientConversationsListPage;
